/**
 * Shared admin-dashboard response contract (issue #1143, epic #1140).
 *
 * Pure TypeScript interfaces describing the redesigned admin-dashboard
 * overview / weekly-capacity / activity responses. These are the single
 * source of truth referenced by both the backend (`admin-dashboard-api`)
 * DTOs and the frontend admin-dashboard store.
 *
 * Intentionally framework-free: no Angular/NestJS imports so both back and
 * front can consume them via `@bodyanalytics/data-models-ui`.
 *
 * NOTE: this is unrelated to the legacy `dashboard.ts` types
 * (`ChartType`/`EChartType`/`Stat`/`Chat`/`Transaction`), which are left intact.
 */

/** A single headline metric with its sparkline series. */
export interface DashboardStatI {
  value: number;
  spark: number[];
}

/** Overview cards rendered at the top of the redesigned admin dashboard. */
export interface DashboardOverviewI {
  eventsThisWeek: DashboardStatI & { deltaVsLastWeek: number };
  testsScheduled: DashboardStatI & { capacityPct: number };
  activePartners: DashboardStatI & { deltaThisMonth: number };
  revenueMtd: { cents: number; spark: number[]; deltaPctVsPrevMonth: number };
}

/** A single day in the weekly-capacity strip. */
export interface WeeklyCapacityDayI {
  day: string;
  date: string;
  booked: number;
  cap: number;
  pct: number;
  today: boolean;
}

/**
 * Activity-feed item kinds.
 *
 * Exactly `'booking' | 'confirmed' | 'partner' | 'cancellation'` — there is
 * intentionally NO `'waitlist'` type (no event-booking waitlist exists; see
 * issue A). Do not add `'waitlist'` unless a waitlist feature is built.
 */
export type DashboardActivityTypeI = 'booking' | 'confirmed' | 'partner' | 'cancellation';

/** A single entry in the admin-dashboard activity feed. */
export interface DashboardActivityItemI {
  id: string;
  type: DashboardActivityTypeI;
  actor: string;
  note: string;
  club: string;
  at: string;
}
